/**
 * Microsoft Clarity, loaded from the entry point rather than from `index.html`.
 *
 * It exists to answer the one question paid traffic cannot: a visitor arrives
 * from a campaign, does not sign up, and nothing says whether they read the page
 * or bounced in two seconds. Those two readings ask for opposite fixes -- rewrite
 * the page, or change the audience -- and choosing between them by guess is what
 * this closes. Scroll depth, rage clicks, dead clicks and session replay.
 *
 * Three things a caller has to know:
 *
 * - **Masking runs in the browser, before upload.** Masked text never leaves the
 *   visitor's machine. The mode is a property of the CLARITY PROJECT, not of this
 *   code, so the id passed here decides which rules apply.
 * - **A project serving personal data must be Strict before it is ever tagged.**
 *   Balanced, the Clarity default, masks digits and e-mail addresses but NOT
 *   names, and a masking change is never retroactive: sessions recorded before
 *   the switch keep whatever they captured. The public site is Balanced because
 *   it carries no personal data; the operator panel is a separate project and
 *   Strict.
 * - **Disclosure is part of shipping it.** Section 10 of the landing's
 *   `privacidade.html` is the promise that measurement would be disclosed when
 *   adopted. Adding a tag without updating it puts the policy out of step with
 *   the site on the same day.
 *
 * Production builds only. A dev server would file every local reload as a real
 * session, which is how a replay list becomes useless. A production build is not
 * a production site, though: the panel's dev environment ships the same build,
 * so a caller deployed on both legs decides by host which one it measures.
 */

const SRC = 'https://www.clarity.ms/tag/';

type ClarityQueue = { q?: unknown[] } & ((...args: unknown[]) => void);

/**
 * Inject the Clarity tag for `projectId`.
 *
 * No-ops outside production, without a document, and on a second call. Never
 * throws: a blocked or failed analytics script must not take the page with it.
 */
export function loadClarity(projectId: string): void {
  if (!import.meta.env.PROD) return;
  if (typeof document === 'undefined' || !projectId) return;

  const w = window as typeof window & { clarity?: ClarityQueue };
  if (w.clarity) return;

  try {
    const queue: ClarityQueue = Object.assign(
      (...args: unknown[]) => {
        (queue.q = queue.q ?? []).push(args);
      },
      {} as { q?: unknown[] },
    );
    w.clarity = queue;

    const tag = document.createElement('script');
    tag.async = true;
    tag.src = SRC + projectId;
    document.head.appendChild(tag);
  } catch {
    // A page that cannot load analytics still has to render.
  }
}
